import "@/lib/zod";
import { useEffect } from "react";
import { useForm as useRHFForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ZodType } from "zod";
import type {
  DefaultValues,
  FieldValues,
  UseFormProps,
  UseFormReturn,
} from "react-hook-form";

interface ReturnType<T extends FieldValues> {
  form: UseFormReturn<T>;
  isSubmitDisabled: boolean;
}

type UseFormOptions<T extends FieldValues> = Omit<
  UseFormProps<T>,
  "resolver"
> & {
  defaultValues?: DefaultValues<T>;
  resetOnDefaultValuesChange?: boolean;
};

export function useForm<T extends FieldValues>(
  schema: ZodType<T>,
  options?: UseFormOptions<T>,
): ReturnType<T> {
  const { resetOnDefaultValuesChange = true, ...formOptions } = options ?? {};

  const form = useRHFForm<T>({
    ...formOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any),
    mode: "all",
  });

  useEffect(() => {
    if (!resetOnDefaultValuesChange) return;
    form.reset(formOptions.defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOptions.defaultValues, resetOnDefaultValuesChange]);

  return {
    form,
    isSubmitDisabled:
      !form.formState.isValid ||
      Object.keys(form.formState.errors).length > 0 ||
      form.formState.isSubmitting,
  };
}
